import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL14_DYNAMIC_SYMBOLS = Object.freeze({
  movePlatform1: { imagePath: "port/generated/levels_fg/level_14/movePlatform1.runtime.svg" },
  bluePlat: { imagePath: "port/generated/levels_fg/level_14/bluePlat.runtime.svg" },
  catapult: { imagePath: "port/generated/levels_fg/level_14/catapult.runtime.svg" },
  dropPlat: { imagePath: "port/generated/levels_fg/level_14/dropPlat.runtime.svg" },
  killRollBall: { imagePath: "port/generated/levels_fg/level_14/killRollBall.runtime.svg" },
  ball40: { imagePath: "port/generated/levels_fg/level_14/ball40.runtime.svg" },
});

const LEVEL14_DYNAMIC_DRAW_ORDER = Object.freeze([
  "movePlatform1",
  "bluePlat",
  "catapult",
  "dropPlat",
  "killRollBall",
  "ball40",
]);

function drawDynamicImage(ctx, image, x, y, rotationDeg = 0) {
  if (!image?.complete) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, -400, -400);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  if (rotationDeg) {
    ctx.rotate((rotationDeg * Math.PI) / 180);
  }
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelFourteenRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_14/1.visual.runtime.svg";
      this.level14DynamicImages = Object.fromEntries(
        Object.entries(LEVEL14_DYNAMIC_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
    } else {
      this.level14DynamicImages = {};
    }

    this.createAllLevelBodies();
    this.movePlatform1Body = this.getBody("movePlatform1");
    this.bluePlatBody = this.getBody("bluePlat");
    this.drop1Body = this.getBody("dropPlat");
    this.killRollBallBody = this.getBody("killRollBall");
    this.addPrismaticJoint(this.movePlatform1Body, this.ground, this.movePlatform1Body.GetWorldCenter(), new b2Vec2(0, 1));
    this.addRevoluteJoint(this.getBody("catapult"), this.getBody("catapultOpora"), this.getBody("catapult").GetPosition());
    this.movePlatform1BodyDirection = 1;
    this.killRollGo = false;
  }

  levelUpdate() {
    if (this.player.y > 750 || this.contactListener.playerContactBodies.indexOf(this.killRollBallBody) !== -1) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    const movePlatform1 = this.getDisplayLayout("movePlatform1");
    if (movePlatform1.y < 232) {
      this.movePlatform1BodyDirection = 1;
    }
    if (movePlatform1.y > 640) {
      this.movePlatform1BodyDirection = -1;
    }
    this.movePlatform1Body.SetLinearVelocity(new b2Vec2(0, 3 * this.movePlatform1BodyDirection));
    if (this.hitNamedObject("blueCheck") && this.bluePlatBody) {
      this.destroyBody(this.bluePlatBody);
      this.bluePlatBody = null;
    }
    if (this.contactListener.playerContactBodies.indexOf(this.drop1Body) !== -1) {
      this.destroyBody(this.drop1Body);
      this.drop1Body = this.createBody("dropPlat", "Polygon", 1, 1, 0.2, [[[-62, 5], [-62, -5], [62, -5], [62, 5]]]);
      this.refreshNamedRects();
    }
    if (this.player.x > 1410 && !this.killRollGo) {
      this.killRollGo = true;
      this.killRollBallBody.ApplyImpulse(new b2Vec2(-5, 0), this.killRollBallBody.GetPosition());
    }
  }

  drawLevel14Bodies(ctx) {
    for (const name of LEVEL14_DYNAMIC_DRAW_ORDER) {
      if (name === "bluePlat" && !this.bluePlatBody) {
        continue;
      }
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      drawDynamicImage(
        ctx,
        this.level14DynamicImages[name],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    this.drawLevel14Bodies(ctx);

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
